import { Select } from 'antd'
import type { FC } from 'react'
import { memo, useCallback, useMemo } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router'
import { PATH_SEGMENT } from 'routes/pathSegments'

import type { MenuItemType } from 'antd/lib/menu/interface'

import { profileMenuConfig } from 'features/Profile/config'
import styles from './menuSelect.module.scss'
import type { MenuSelectProps } from './menuSelect.types'

const { Option, OptGroup } = Select

type MenuItem = MenuItemType & {
  children?: MenuItem[]
}

export const MenuSelect: FC<MenuSelectProps> = memo(({ isStaff, hasEmrEnabled }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { siteId } = useParams<{ siteId: string }>()

  const navigableKeys = useMemo(
    () => profileMenuConfig.getNavigableKeys(isStaff, hasEmrEnabled),
    [isStaff, hasEmrEnabled],
  )

  const handleChange = useCallback(
    (value: string) => {
      if (!navigableKeys.includes(value)) {
        return
      }

      navigate(`/${siteId}/${PATH_SEGMENT.PROFILE}/${value}`)
    },
    [navigate, siteId, navigableKeys],
  )

  const currentPath = useMemo(() => {
    if (location.pathname.includes(`/${PATH_SEGMENT.PRESCRIPTIONS}/`)) {
      return PATH_SEGMENT.PRESCRIPTIONS
    }

    return location.pathname.split('/').at(-1) || PATH_SEGMENT.PERSONAL_PROFILE
  }, [location])

  const renderMenuItem = (item: MenuItem) => {
    if (item.children) {
      return (
        <OptGroup key={item.key} label={item.label}>
          {item.children.map(renderMenuItem)}
        </OptGroup>
      )
    } else {
      return (
        <Option key={item.key} value={item.key?.toString()}>
          <span>{item.label}</span>
        </Option>
      )
    }
  }

  return (
    <Select className={styles.menu} value={currentPath} onChange={handleChange}>
      {profileMenuConfig
        .getMenus(isStaff, hasEmrEnabled)
        .map((item) => renderMenuItem(item as MenuItem))}
    </Select>
  )
})
